import React, { useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiLogOut, FiUser } from "react-icons/fi";

import profile from "../restaurant/assets/profile.webp";
import MayaBrandMark from "../components/MayaBrandMark";
import { performLogout } from "../utils/sessionManager";
import { canManageEatsEmployees, isEatsAppManager } from "../utils/eatsAppAuth";
import PartnerBusinessSwitcher from "../partner/PartnerBusinessSwitcher";
import { ROUTES, supportUrl } from "../constants/appRoutes";
import { formatUserFullName } from "../restaurant/formatDisplay";
import "../restaurant/css/Header.css";
import "./EatsAdminLayout.css";

export const ADMIN_SIDEBAR_ITEMS = [
  ["home", "Dashboard"],
  ["users", "Employees"],
  ["restaurants", "Restaurants"],
  ["organizations", "Organizations"],
  ["stores", "Stores"],
  ["customers", "Customers"],
  ["subscriptions", "Subscriptions"],
  ["jreats-menu", "Jr Eats Menu"],
  ["menu-master", "Menu Master"],
  ["pickup-hubs", "Pickup Hubs"],
];

/** Sidebar tabs that open dedicated routes instead of dashboard query tabs. */
const ADMIN_SIDEBAR_ROUTES = {
  users: ROUTES.adminEmployees,
};

export const ADMIN_HEADER_OFFSET = 74;

export function EatsAdminHeader() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const displayName = formatUserFullName(user, "App Admin");

  const openSupportWindow = () => {
    const width = 720;
    const height = 720;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;
    const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`;
    const newWindow = window.open(
      supportUrl({ type: "business", name: "Maya Eats Business" }),
      "_blank",
      features
    );
    if (!newWindow) {
      alert("Please allow popups for this website");
    }
  };

  const handleSignOut = () => {
    performLogout({ redirectPath: ROUTES.partnerLogin });
  };

  return (
    <header className="restaurant-header">
      <div className="jr-eatslogo-section">
        <MayaBrandMark variant="header" />
      </div>
      <div className="res-header-container1">
        <button type="button" className="btn-sprt" onClick={openSupportWindow}>
          Support
        </button>
        <div className="maya-account-menu">
          <button type="button" className="maya-account-menu__trigger" aria-haspopup="menu">
            <img src={profile} alt="" className="maya-account-menu__avatar" />
            <span className="maya-account-menu__name">{displayName}</span>
          </button>
          <div className="maya-account-menu__panel" role="menu">
            <div className="maya-account-menu__summary">
              <img src={profile} alt="" className="maya-account-menu__summary-avatar" />
              <div className="maya-account-menu__summary-text">
                <strong>{displayName}</strong>
                <span>{user?.email || user?.userName || "Maya Eats Business"}</span>
              </div>
            </div>
            <div className="maya-account-menu__divider" />
            <Link to={ROUTES.userProfile} className="maya-account-menu__item" role="menuitem">
              <FiUser aria-hidden="true" />
              <span>Profile</span>
            </Link>
            <PartnerBusinessSwitcher />
            <div className="maya-account-menu__divider" />
            <button
              type="button"
              className="maya-account-menu__item maya-account-menu__item--danger"
              role="menuitem"
              onClick={handleSignOut}
            >
              <FiLogOut aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

const EatsAdminLayout = ({ activeTab = "home", flushMain = false, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const canManageEmployees = canManageEatsEmployees(user);
  const sidebarItems = isEatsAppManager(user)
    ? ADMIN_SIDEBAR_ITEMS.filter(([id]) => (id === "users" ? canManageEmployees : true))
    : [];
  const dashboardBase = location.pathname.startsWith("/eats/user") ? ROUTES.user : ROUTES.admin;

  return (
    <>
      <EatsAdminHeader />

      <div className="eats-admin-shell">
        <div className="eats-admin-body">
          <aside className="eats-admin-sidebar">
            {sidebarItems.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`eats-admin-sidebar-btn${activeTab === id ? " is-active" : ""}`}
                onClick={() => {
                  const route = ADMIN_SIDEBAR_ROUTES[id];
                  if (route) {
                    navigate(route);
                    return;
                  }
                  if (!id || id === "home") {
                    navigate(dashboardBase);
                    return;
                  }
                  navigate(`${dashboardBase}?tab=${encodeURIComponent(id)}`);
                }}
              >
                {label}
              </button>
            ))}
          </aside>

          <main className={`eats-admin-main${flushMain ? " eats-admin-main--flush" : ""}`}>{children}</main>
        </div>
      </div>
    </>
  );
};

export default EatsAdminLayout;
